/* ORYX App 2.0
 *
 * Created by ORYX Movement Solutions © 2022
 * ==================================================================
 *
 * Homepage.
 */

import {
  IonButton,
  IonCardContent,
  IonCol,
  IonContent,
  IonGrid,
  IonIcon,
  IonModal,
  IonPage,
  IonProgressBar,
  IonRow,
  IonSpinner,
  IonText,
  isPlatform,
  useIonAlert,
  useIonToast,
} from '@ionic/react';
import { checkmarkCircleSharp } from 'ionicons/icons';
import * as React from 'react';
import { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { CartesianGrid, Legend, Line, LineChart, Tooltip, XAxis, YAxis } from 'recharts';

import { ActiveRecordingTimer } from '@/components/ActiveRecordingTimer/ActiveRecordingTimer';
import { BottomBar } from '@/components/BottomBar/BottomBar';
import { Button } from '@/components/Button';
import { CalibrateLoading } from '@/components/CalibrateLoading/CalibrateLoading';
import { CalibrationCompleted } from '@/components/CalibrationCompleted/CalibrationCompleted';
import { CalibrationCountdown } from '@/components/CalibrationCountdown/CalibrationCountdown';
import { CalibrationFailed } from '@/components/CalibrationFailed/CalibrationFailed';
import { CardHeader } from '@/components/CardHeader';
import { Card } from '@/components/Cards';
import { ChooseMeasurementModal } from '@/components/ChooseMeasurementModal/ChooseMeasurementModal';
import { RadioSelectButtonGroup } from '@/components/RadioSelectButtonGroup/RadioSelectButtonGroup';
import { SensorCard, SensorStatus } from '@/components/SensorCard/SensorCard';
import { SensorFlowModal } from '@/components/SensorFlowModal/SensorFlowModal';
import { SensorPlacement } from '@/components/SensorPlacement/SensorPlacement';
import { Stepper } from '@/components/Stepper/Stepper';
import { TagModal } from '@/components/TagModal/TagModal';
import { Toast } from '@/components/Toasts';

import { uuid } from '@/utils';
import Logger from '@/utils/CapcitorLog';
import { DebugOverlay } from '@/utils/DebugOverlay';

import { useAuthContext } from '@/contexts/authContext';

import { NativeQSenseControl } from '@/plugins/NativeQSenseControl';
import { QSenseHubControl } from '@/plugins/QSenseHubControl';
import { USBDetection, USBDevice } from '@/plugins/USBDetection';
import { useUSBDetection } from '@/plugins/USBDetection/USBDetectionHook';
interface AccelerometerGraphProps {
  data: Array<{ time: number; x: number; y: number; z: number }>;
}

const AccelerometerGraph: React.FC<AccelerometerGraphProps> = ({ data }) => {
  return (
    <LineChart data={data} height={400} margin={{ top: 20, right: 30, left: 20, bottom: 5 }} width={800}>
      <CartesianGrid strokeDasharray='3 3' />
      <XAxis dataKey='time' label={{ value: 'Time', position: 'insideBottom', offset: -10 }} />
      <YAxis label={{ value: 'Acceleration', angle: -90, position: 'insideLeft' }} />
      <Tooltip />
      <Legend />
      <Line dataKey='x' name='X-axis' stroke='#8884d8' type='monotone' />
      <Line dataKey='y' name='Y-axis' stroke='#82ca9d' type='monotone' />
      <Line dataKey='z' name='Z-axis' stroke='#ffc658' type='monotone' />
    </LineChart>
  );
};

interface ComponentState {
  isConnected: boolean;
  devices: USBDevice[];
  error: string | null;
}

export const SensorTestingPage = () => {
  const [showSessionModal, setShowSessionModal] = useState(false);
  const [showTagModal, setShowTagModal] = useState(false);
  console.log('SensorTestingPage');
  const [isPlaying, setIsPlaying] = useState(false);
  const history = useHistory();
  const platform = isPlatform('capacitor') ? 'capacitor' : 'web';
  const userId = useAuthContext().state.userState?.user.uid;
  const [presentAlert] = useIonAlert();
  const [scanning, setScanning] = useState(false);
  const [discoveredDevices, setDiscoveredDevices] = useState<string[]>([]);
  const [connectedDevices, setConnectedDevices] = useState<string[]>([]);
  const [motionLevels, setMotionLevels] = useState<{ [device: string]: number }>({});
  const [batteryLevels, setBatteryLevels] = useState<{ [device: string]: number }>({});
  const [present] = useIonToast();
  const [data, setData] = useState([]);

  // TODO: Get this from the database
  const allowedDevices = [
    'FE:82:64:29:48:E9',
    'ED:9B:08:6A:C2:C7',
    'FB:8F:72:1D:63:9B',
    'F2:A3:65:5F:EC:80',
    'C6:27:4B:1F:BA:AE',
    'C3:0D:C0:66:3D:49',
    'DD:EA:9F:5B:4E:86',
    'ED:9B:08:6A:C2:C7',
  ];

  // HUB
  const { isConnected, devices, selectedFile, error, checkConnection, checkFile, clearError, writeFileHeader } =
    useUSBDetection();

  useEffect(() => {
    if (isConnected) {
      Logger.info('Connected to device');
    }
    if (selectedFile) {
      Logger.info('Selected file: ' + selectedFile);
    }
  }, [isConnected, selectedFile]);

  const writeFile = async () => {
    try {
      await writeFileHeader({
        timeStart: Math.floor(Date.now() / 1000),
        buttonStartEnable: true,
        whitelist: ['00:11:22:33:44:55'],
        deviceState: {
          enableOffsetCompensation: true,
          accSensitivity: 1,
          gyrSensitivity: 6,
        },
        dataMode: 0,
      });
    } catch (error) {
      console.error('Error writing file header', error);
    }
  };

  return (
    <>
      {/* <IonModal isOpen={showModal}>
        <AccelerometerGraph data={data} />
      </IonModal> */}

      <TagModal isOpen={showTagModal} setShowModal={setShowTagModal} />
      <ChooseMeasurementModal isOpen={showSessionModal} setShowModal={setShowSessionModal} />

      <Toast isOpen={!!error} message={error!} onDidDismiss={clearError} type='warning' />

      <IonPage id='main'>
        <IonContent className='ion-padding' color='light' fullscreen id='main'>
          <IonGrid className='full_height' fixed>
            <IonRow className='ion-no-padding full_height'>
              <Card style={{ width: '100%', height: '100%' }}>
                <CardHeader title='Hub Testing' />
                <IonCardContent>
                  <IonRow>
                    <Button onClick={() => setShowSessionModal(true)}>Show Session modal</Button>
                    <Button onClick={() => setShowTagModal(true)}>Show Tag modal</Button>
                  </IonRow>
                  <Button onClick={() => checkConnection()}>Open Document Picker</Button>
                  <Button onClick={() => checkFile()}>Check File</Button>
                  <Button onClick={() => USBDetection.pickFileToCopy()}>Copy File</Button>

                  <Button onClick={() => writeFile()}>Write File</Button>
                  <Button onClick={() => QSenseHubControl.startScan()}>Start Scan</Button>
                </IonCardContent>
                {/* {state.isConnected && state.devices.length > 0 && (
                  <div>
                    <h3>Connected Devices:</h3>
                    {state.devices.map((device, index) => (
                      <div key={device.serialNumber || index}>
                        <h4>{device.name}</h4>
                        <p>Manufacturer: {device.manufacturer}</p>
                        <p>Model: {device.modelNumber}</p>
                        <p>Serial: {device.serialNumber}</p>
                      </div>
                    ))}
                  </div>
                )} */}
              </Card>
            </IonRow>
            <IonRow>
              <Card>
                <CardHeader title='Sensor Testing' />
                <IonCardContent className='' style={{ height: '95%' }}>
                  <IonRow className='full_height'>
                    <IonCol className='' size='4'>
                      <IonButton
                        className='primary'
                        onClick={() => {
                          console.log('Start Scan button clicked!');
                          NativeQSenseControl.startScan({ allowedDevices: allowedDevices })
                            .then((result) => setScanning(true))
                            .catch((error) => console.error(error));
                        }}>
                        {scanning ? <IonSpinner name='bubbles' /> : 'Start Scanning'}
                      </IonButton>
                      <IonButton
                        className='primary'
                        disabled={!scanning}
                        onClick={() => {
                          console.log('Stop Scan button clicked!');
                          NativeQSenseControl.stopScan()
                            .then((result) => setScanning(false))
                            .catch((error) => console.error(error));
                        }}>
                        Stop scanning
                      </IonButton>
                      <IonButton className='primary' onClick={() => NativeQSenseControl.connectDevices()}>
                        Connect Sensors
                      </IonButton>
                      <IonButton className='primary' onClick={() => NativeQSenseControl.setDataMode()}>
                        Set DataMode
                      </IonButton>
                      <IonButton className='primary' onClick={() => NativeQSenseControl.startStreaming()}>
                        Start Streaming
                      </IonButton>
                      <IonButton className='primary' onClick={() => NativeQSenseControl.stopStreaming()}>
                        Stop Streaming
                      </IonButton>
                      <IonButton
                        className='primary'
                        onClick={() => {
                          NativeQSenseControl.disConnectDevices();
                          console.log('Disconnect Sensors button clicked!');
                        }}>
                        Disconnect Sensors
                      </IonButton>
                    </IonCol>
                    <IonCol className='' size='8'>
                      {discoveredDevices.map((device) => (
                        <IonRow key={uuid()}>
                          <IonCol>
                            <IonText>
                              {device}{' '}
                              {connectedDevices.includes(device) ? (
                                <IonIcon color='success' icon={checkmarkCircleSharp} />
                              ) : null}
                            </IonText>
                          </IonCol>
                          {connectedDevices.includes(device) && (
                            <>
                              <IonCol>
                                <IonText>{batteryLevels[device] || 0}%</IonText>
                              </IonCol>
                              <IonCol>
                                {/* <Button onClick={() => fetchData(device, 'accelerometer')}>Fetch Accelerometer</Button> */}
                              </IonCol>
                              <IonCol>
                                <Button onClick={() => setShowSessionModal(true)}>Show data</Button>
                              </IonCol>
                              <IonProgressBar color='secondary' value={motionLevels[device] || 0}></IonProgressBar>
                            </>
                          )}
                        </IonRow>
                      ))}
                    </IonCol>
                  </IonRow>
                  <SensorPlacement
                    centerPelvis={'hidden'}
                    leftCalf={'error'}
                    leftFoot={'not_calibrated'}
                    leftPelvis={'selected'}
                    leftThigh={'not_calibrated'}
                    rightCalf={'not_calibrated'}
                    rightFoot={'not_calibrated'}
                    rightPelvis={'not_calibrated'}
                    rightThigh={'not_calibrated'}
                  />
                </IonCardContent>
              </Card>

              <IonRow style={{ gap: 16 }}>
                <SensorCard
                  onSelect={function (id: string): void {
                    throw new Error('Function not implemented.');
                  }}
                  selected={false}
                  sensorId='SIMULATED_DEVICE_001'
                  state={SensorStatus.CALIBRATING}
                  title='Legs'
                />
                <SensorCard
                  onSelect={function (id: string): void {
                    throw new Error('Function not implemented.');
                  }}
                  selected={false}
                  sensorId='FB:8F:72:1D:63:9B'
                  state={SensorStatus.SKELETON}
                  title='Legs'
                />
                <SensorCard
                  onSelect={function (id: string): void {
                    throw new Error('Function not implemented.');
                  }}
                  selected={false}
                  sensorId='FB:8F:72:1D:63:9B'
                  state={SensorStatus.CONNECTED_NOT_CALIBRATED}
                  title='Legs'
                />
                <SensorCard
                  onSelect={function (id: string): void {
                    throw new Error('Function not implemented.');
                  }}
                  selected={false}
                  sensorId='FB:8F:72:1D:63:9B'
                  state={SensorStatus.SEARCHING}
                  title='Legs'
                />
                <SensorCard
                  onSelect={function (id: string): void {
                    throw new Error('Function not implemented.');
                  }}
                  selected={true}
                  sensorId='FB:8F:72:1D:63:9B'
                  state={SensorStatus.SEARCHING}
                  title='Legs'
                />
              </IonRow>
            </IonRow>

            <IonRow>
              <CalibrationCompleted description='Continue to start recording' title='Calibration completed' />
            </IonRow>

            <Stepper
              currentStep={1}
              isNextDisabled={false}
              onStepChange={function (step: number): void {
                throw new Error('Function not implemented.');
              }}
              steps={[
                {
                  name: 'Connect sensors',
                  title: 'Connect sensors',
                  description: 'Connect sensors to the device',
                },
                {
                  name: 'Reset orientation',
                  title: 'Reset orientation',
                  description: 'Reset orientation to the device',
                },
                {
                  name: 'Place sensors',
                  title: 'Place sensors',
                  description: 'Place sensors on the device',
                },
                {
                  name: 'Calibrate',
                  title: 'Calibrate',
                  description: 'Calibrate the device',
                },
              ]}
            />

            <IonRow>
              <CalibrateLoading />
            </IonRow>

            <IonRow>
              <SensorFlowModal description='Plug *in* the sensors' illustration='plugIn' />
              <SensorFlowModal buttonText='End recording' description='Plug *in* the sensors' illustration='success' />
              <SensorFlowModal description='Plug *in* the sensors' illustration='unplug' />
              <SensorFlowModal description='Plug *in* the sensors' illustration='syncing' progress={0.3} />
            </IonRow>

            <IonRow>
              <CalibrationFailed description='Movement detected, please re-calibrate' title='Calibration failed' />
            </IonRow>

            <RadioSelectButtonGroup
              activeId='4'
              onChange={() => console.log('onChange')}
              options={[
                { id: '1', label: 'Option 1', icon: 'search' },
                { id: '2', label: 'Option 2', icon: 'search' },
                { id: '3', label: 'Option 3', icon: 'search' },
                { id: '4', label: 'Option 4', icon: 'search' },
              ]}
            />
            <ActiveRecordingTimer startTime={Date.now() - 1000} />

            <BottomBar
              description='Ready to start recording! Select your exercise type and tap start recording'
              nextButton={{
                label: 'Continue',
                onClick: () => {
                  console.log('This should change state');
                },
              }}
              title='Recording'
            />

            <CalibrationCountdown isPlaying={isPlaying} />
            <Button onClick={() => setIsPlaying(true)}>Start countdown</Button>
          </IonGrid>
        </IonContent>
        <DebugOverlay initiallyVisible={true} />
      </IonPage>
    </>
  );
};
